import React, { useState } from "react";
import ReactModal from "react-modal";
import { toast } from "react-toastify";
import countryCodes from "../../../CountryCode/CountryCode.json";
import AuthUser from "../../../Hooks/authUser";

const SuperAdminRessellerModal = ({ isOpen, closeModal }) => {
  const { userInfo } = AuthUser();
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [countryCode, setCountryCode] = useState(
    countryCodes[0]?.dial_code || ""
  );

  const createUser = async (user) => {
    const response = await fetch("https://servercareseebd.careseebd.com/api/v1/user/signup", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${localStorage.getItem("careSeeAccessToken")}`,
      },
      body: JSON.stringify(user),
    });

    if (!response.ok) {
      const responseData = await response.json();
      throw new Error(responseData.message || "Failed to create Reseller.");
    }
  };

  const handelSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const name = e.target.name.value;
    const email = e.target.email.value;
    const phoneWithCountryCode = `${countryCode}${phone}`;
    const password = e.target.password.value;

    const user = {
      name,
      email,
      password,
      phone: phoneWithCountryCode,
      confirmPassword: password,
      role: "admin",
    };

    try {
      await createUser(user);
      toast.success("Reseller created successfully!");
      e.target.reset();
      setPhone("");
      closeModal();
    } catch (error) {
      toast.error(error.message || "An error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full py-2.5 px-4 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-500/10 transition-all duration-200";

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={closeModal}
      className="fixed z-50 inset-0 flex items-center justify-center p-4"
      overlayClassName="fixed inset-0 z-40"
      style={{
        overlay: {
          backgroundColor: "rgba(15, 23, 42, 0.6)",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
        },
        content: {
          border: "none",
          background: "transparent",
          padding: 0,
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
      }}
    >
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header gradient bar */}
        <div className="h-1.5 w-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500" />

        <div className="px-8 pt-7 pb-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-7">
            <div>
              <h2 className="text-xl font-bold text-gray-900 tracking-tight">
                Add New Reseller
              </h2>
              <p className="text-sm text-gray-400 mt-0.5">
                Create a reseller admin account
              </p>
            </div>
            <button
              type="button"
              onClick={closeModal}
              className="flex items-center justify-center w-9 h-9 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handelSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                Full Name
              </label>
              <input
                className={inputClass}
                type="text"
                name="name"
                required
                placeholder="e.g. John Doe"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                Email Address
              </label>
              <input
                className={inputClass}
                type="email"
                name="email"
                required
                placeholder="john@example.com"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                Phone Number
              </label>
              <div className="flex gap-2">
                <select
                  className="py-2.5 px-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 focus:outline-none focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-500/10 transition-all duration-200 w-36 shrink-0"
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                >
                  {countryCodes.map((country) => (
                    <option key={country.code} value={country.dial_code}>
                      {country.dial_code} {country.name}
                    </option>
                  ))}
                </select>
                <input
                  className={inputClass}
                  type="number"
                  name="phone"
                  required
                  placeholder="Phone number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                Password
              </label>
              <input
                className={inputClass}
                type="password"
                name="password"
                required
                placeholder="Set a strong password"
              />
            </div>

            <div className="pt-3">
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-6 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-sm font-semibold rounded-xl shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg
                      className="animate-spin h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    Creating...
                  </span>
                ) : (
                  "+ Add Reseller"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </ReactModal>
  );
};

export default SuperAdminRessellerModal;
